// Is this server `error` string an INTERNAL diagnostic rather than operator copy?
//
// Several Rails controllers answer 4xx with a developer-facing string built from
// the class and method that refused, e.g.
//
//   "PickingOrdersController::put_in_review -> Cannot send from packing to review
//    order 123. Invalid statuses found for stock units of product_id 45."
//
// Rendering that at a packing bench tells the operator nothing they can act on
// (BUGS-8257 review; the review-exit gap itself is tracked as BUGS-8501). Callers
// use this to substitute localized copy while still passing through the many
// server messages that ARE written for the operator — so the test is deliberately
// narrow: only the shapes that can only have come from code.
//
//   1. the string names a Rails controller (`FooController::bar`, `FooController#bar`);
//   2. it OPENS with a constant-and-method prefix followed by `->` or `:`
//      (`Foo::bar ->`, `Foo::Bar::baz:`, `Foo#bar ->`) — the convention those
//      messages are built with.
//
// A message that merely mentions a class name mid-sentence is NOT internal by this
// test: a false positive would hide a real, actionable server refusal.
const CONTROLLER_MENTION = /[A-Za-z0-9_]Controller(::|#)/
const CLASS_METHOD_PREFIX = /^\s*[A-Z][A-Za-z0-9_]*(?:::[A-Za-z0-9_]+)*(?:::|#|\.)[A-Za-z0-9_?!]+\s*(?:->|:)/

// "The server told us NOTHING" is a different situation from "the server told us
// something internal-looking", and it must not borrow the other one's copy: a
// network drop, a timeout or a 500 with an HTML body all arrive here with no
// `error` field, and naming a specific wrong cause ("there are already packed
// pieces") is worse than saying the request did not go through. Callers ask this
// FIRST and use generic retry copy when it is true.
export const isMissingServerMessage = message => typeof message !== 'string' || message.trim() === ''

// False for a message-less failure: there is no string to classify, and the caller
// has separate copy for that case (isMissingServerMessage).
export const looksLikeInternalServerError = message => {
  if (isMissingServerMessage(message)) return false
  return CONTROLLER_MENTION.test(message) || CLASS_METHOD_PREFIX.test(message)
}

// The ONE put_in_review refusal we can identify from its text: units of a product are
// no longer all PICKED, i.e. pieces are already packed. `put_in_review` raises SIX
// refusals that all carry the `PickingOrdersController::put_in_review ->` prefix — the
// order is not in `packing`, it is already in review, it is a REMOVE_DAMAGED /
// REMOVE_EXPIRED type, the quantity or missing-quantity checks failed, and this one — so
// the prefix alone cannot pick a cause, and f0c237a6 made the wrong-status one newly
// reachable by relaxing when the button is enabled. Matching the fragment is what
// actually identifies the packed-pieces case. (BUGS-8501 tracks the refusal itself.)
const PACKED_PIECES_REFUSAL = /Invalid statuses found for stock units of product_id/i

const REVIEW_COPY = 'generate_shipment.missing_picking_pieces.'

// Which copy the review / missing-info exit should show for a failed put_in_review.
// Returns EITHER { key } — an i18n key the caller translates — OR { text }, a server
// message that is written for the operator and passes through unchanged.
//
// Four cases, in this order:
//   1. nothing to show, or ANY 5xx. A 500 answering { error: "unexpected error" } is a
//      non-empty string that names no class, so the shape tests let it through and the
//      English words reached the packing bench. Keyed on the status, not on a blocklist
//      of English strings: a 5xx body is never operator copy.
//   2. the packed-pieces refusal → its specific copy, which names a cause AND a recovery
//      (reopen the cajas, unscan) that only works for that cause.
//   3. any other internal-shaped diagnostic → generic copy that names NO cause. Telling
//      an operator "there are already packed pieces" on a shipped order is a false cause
//      plus a recovery that cannot work, and we cannot tell the remaining five apart
//      well enough to write copy for each.
//   4. anything else → the server's own message, which is usually written for them.
export const resolveReviewRefusalCopy = ({ status, message, eraseShippingData = false } = {}) => {
  if (isMissingServerMessage(message) || Number(status) >= 500) return { key: `${REVIEW_COPY}request_failed_retry` }

  if (PACKED_PIECES_REFUSAL.test(message)) {
    return { key: `${REVIEW_COPY}${eraseShippingData ? 'missing_info_blocked_pieces_packed' : 'review_blocked_pieces_packed'}` }
  }

  if (looksLikeInternalServerError(message)) return { key: `${REVIEW_COPY}request_refused_report` }

  return { text: message }
}

// Did put_in_review refuse before it touched the packing bench?
//
// On an order packed on the multi-box screen the server clears the bench (deletes every box,
// returns the pieces to PICKED) before it walks the order back to picking. Every refusal it
// can judge up front comes before that and answers 422, so the dialog can stay open for the
// operator to fix the counts. Two codes, any 5xx and a missing response can follow a bench
// that is already partly or fully cleared: the server no longer knows which pieces were in a
// box, and resending the same counts can declare missing a piece that is on the bench.
const CLEARED_BENCH_CODES = ['stock_moved_during_review', 'order_not_in_packing']

export const reviewRefusalLeftBenchIntact = ({ status, code } = {}) =>
  Number(status) === 422 && !CLEARED_BENCH_CODES.includes(code)
